import {
  type DeliverableId,
  type LicenseTerm,
  type RateCard,
} from '@adthlete/common/utils/dealPricing'

/** What the brand is booking, in the offer form */
export type Booking = {
  kind: 'rate_card' | 'custom'
  themeId: string | null
  deliverable: DeliverableId
  quantity: number
  unitPrice: string
  licenseDays: LicenseTerm | null
  description: string
  customAmount: string
  askForRate: boolean
}

export const emptyBooking = (card: RateCard | null | undefined): Booking => {
  const reel = card?.deliverables.ig_reel
  return {
    kind: card ? 'rate_card' : 'custom',
    themeId: card?.themes?.[0]?.id ?? null,
    deliverable: 'ig_reel',
    quantity: 1,
    unitPrice: reel ? String(midpoint(reel)) : '',
    licenseDays: null,
    description: '',
    customAmount: '',
    askForRate: false,
  }
}

/** A booking started from the storefront: the theme and format picked, at the card's price */
export function bookingFor(
  card: RateCard | null | undefined,
  pick:
    | { kind: 'rate_card'; themeId: string | null; deliverable: DeliverableId }
    | { kind: 'custom'; themeId: string | null }
    | { kind: 'item'; themeId: null; itemId: string },
): Booking {
  const base = emptyBooking(card)
  // One of their own items: a custom booking, described and priced from the item
  if (pick.kind === 'item') {
    const item = card?.items?.find((i) => i.id === pick.itemId)
    return {
      ...base,
      kind: 'custom',
      description: item
        ? `${item.label}${item.description ? `: ${item.description}` : ''} (${item.unit})`
        : '',
      customAmount: item ? String(midpoint(item)) : '',
    }
  }
  const themeId = pick.themeId ?? base.themeId
  if (pick.kind === 'custom' || !card) return { ...base, kind: 'custom', themeId }
  const e = card.deliverables[pick.deliverable]
  return {
    ...base,
    themeId,
    deliverable: pick.deliverable,
    unitPrice: e ? String(midpoint(e)) : '',
  }
}

export const midpoint = (e: { low: number; high: number }) =>
  Math.round((e.low + e.high) / 2 / 5) * 5

/** The platform and format a booked deliverable becomes on the deal */
export const DELIVERABLE_FORMAT: Record<DeliverableId, { platform: string; format: string }> = {
  ig_reel: { platform: 'Instagram', format: 'Reel' },
  tiktok_video: { platform: 'TikTok', format: 'Video' },
  ig_story: { platform: 'Instagram', format: 'Story' },
  static_post: { platform: 'Instagram', format: 'Post' },
  appearance: { platform: 'In person', format: 'Appearance' },
}

/** Price per deliverable times how many, plus licensing if added */
export function bookingTotal(b: Booking, card: RateCard | null | undefined) {
  if (b.kind === 'custom') return b.askForRate ? null : Number(b.customAmount) || 0
  const unit = Number(b.unitPrice) || 0
  const license = b.licenseDays && card ? (card.licensing[b.licenseDays]?.amount ?? 0) : 0
  return unit * b.quantity + license * (b.deliverable === 'appearance' ? 0 : b.quantity)
}
